import { cn, Shimmer, Stack } from 'component-library';
import i18next from 'i18next';
import React, { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useGetPlots } from '@/api/rest/resources/plot';
import { useGetProjectById } from '@/api/rest/resources/project';
import { ContractList } from '@/api/rest/resources/types/contract';
import { AgricultureProjectThumbnail } from '@/components/MapThumbnail/MapThumbnail';
import { Currency } from '@/components/widgets/m/Currency/Currency';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { paths } from '@/routing';
import { UnexpectedMissingDataError } from '@/utils/errors/UnexpectedMissingDataError';

import { useLandstewardContract } from '../../../hooks/useLandstewardContract';

export const List: React.FC = () => {
  const { t } = useTranslation();
  const { data } = useLandstewardContract();

  if (!data) {
    throw new UnexpectedMissingDataError({ dataLabel: 'landStewardContract' });
  }

  return (
    <Stack spacing={6}>
      <span className='typography-overline'>{t('landSteward.investments.labels.activeInvestmentContracts')}</span>
      <Stack spacing={4}>
        {data.results.map((contract, index) => (
          <ListItem key={`${contract.project}_${index}`} {...contract} />
        ))}
      </Stack>
    </Stack>
  );
};

const ListItem: FC<ContractList> = ({
  project: projectId,
  start_date: startDate,
  end_date: endDate,
  carbon_storage_claims: carbonStorageClaims,
  water_holding_capacity_claims: waterHoldingCapacityClaims,
  total_investment: totalInvestment,
}) => {
  const { t } = useTranslation();
  const projectQuery = useGetProjectById(
    { pathVariables: { id: projectId } },
    { throwOnError: true, staleTime: 1000 * 60 * 5 },
  );
  const project = projectQuery.data;

  const plotsQuery = useGetPlots(
    { queryParams: { project: projectId } },
    { throwOnError: true, staleTime: 1000 * 60 * 5 },
  );
  const plots = plotsQuery.data?.results;

  const formattedStartDate = new Date(startDate).toLocaleDateString(i18next.language);
  const formattedEndDate = new Date(endDate).toLocaleDateString(i18next.language);
  const formattedCarbonStorage = useDisplayNumber(carbonStorageClaims ?? 0);
  const formattedWaterHoldingCapacity = useDisplayNumber(waterHoldingCapacityClaims ?? 0);

  const totalInvestmentNumber = (() => {
    if (totalInvestment === undefined) {
      return 0;
    }
    if (Number.isNaN(Number(totalInvestment))) {
      Logger.errorOnce(
        `Error while calculating total investment for contract of project ${projectId}. ${totalInvestment} is not a number.`,
      );
      return 0;
    }

    return Number(totalInvestment);
  })();

  return (
    <div className='rounded-lg bg-white-100 px-6 py-4'>
      <div className='grid w-full grid-cols-none gap-4 divide-y divide-divider lg:grid-cols-12 lg:gap-0 lg:divide-x lg:divide-y-0'>
        <div className='col-span-12 flex flex-row items-center gap-4 lg:col-span-3'>
          {plots ? (
            <AgricultureProjectThumbnail plots={plots} className='h-16 w-16 rounded-lg' />
          ) : (
            <Shimmer className='h-16 w-16 flex-shrink-0 rounded-lg' />
          )}
          <DataStack className='flex-col items-start pt-0 lg:pl-0'>
            <DataStack.Label>{t('landSteward.investments.labels.projectName')}</DataStack.Label>
            <Link to={paths.landSteward.projectDetails.replace(':projectId', projectId)}>
              <DataStack.Data className='hover:underline'>{project?.name}</DataStack.Data>
            </Link>
          </DataStack>
        </div>
        <div className='col-span-12 grid grid-cols-12 items-center gap-4 divide-y divide-divider lg:col-span-9 lg:grid-cols-12 lg:gap-0 lg:divide-x lg:divide-y-0'>
          <DataStack>
            <DataStack.Label>{t('landSteward.investments.labels.startDate')}</DataStack.Label>
            <DataStack.Data>{formattedStartDate}</DataStack.Data>
          </DataStack>
          <DataStack>
            <DataStack.Label>{t('landSteward.investments.labels.endDate')}</DataStack.Label>
            <DataStack.Data>{formattedEndDate}</DataStack.Data>
          </DataStack>
          <DataStack className='lg:col-span-3'>
            <DataStack.Label>{t('global.analysis.carbonStorageBg')}</DataStack.Label>
            <DataStack.Data>
              {t('landSteward.investments.labels.upToUpliftUnits', { num: formattedCarbonStorage })}
            </DataStack.Data>
          </DataStack>
          <DataStack className='lg:col-span-3'>
            <DataStack.Label>{t('global.analysis.waterHoldingCapacity')}</DataStack.Label>
            <DataStack.Data>
              {t('landSteward.investments.labels.upToUpliftUnits', { num: formattedWaterHoldingCapacity })}
            </DataStack.Data>
          </DataStack>
          <DataStack>
            <DataStack.Label>{t('landSteward.investments.labels.totalInvestment')}</DataStack.Label>
            <DataStack.Data>
              <Currency value={totalInvestmentNumber} />
            </DataStack.Data>
          </DataStack>
        </div>
      </div>
    </div>
  );
};

const DataStack = ({ className, ...delegated }: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        'col-span-12 flex flex-row items-center justify-between gap-4 pt-4',
        'lg:col-span-2 lg:flex-col lg:items-start lg:pl-4 lg:pt-0',
        className,
      )}
      {...delegated}
    />
  );
};

DataStack.Label = function SectionLabel({ className, ...delegated }: HTMLAttributes<HTMLDivElement>) {
  return <span className={cn('typography-overline text-text-secondary', className)} {...delegated} />;
};

DataStack.Data = function SectionData({ className, children, ...delegated }: HTMLAttributes<HTMLDivElement>) {
  if (!children) return <Shimmer className='h-5 w-36 rounded-full' data-testid='loading-widget' />;

  return (
    <span className={cn('typography-body1 text-right lg:text-left', className)} {...delegated}>
      {children}
    </span>
  );
};
